import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import ArrowRightLine from '@/assets/icons/arrow_right_line.svg?component';
import { Button } from '@/components/atoms/Buttons';
import { FootNote } from '@/components/atoms/FootNote';
import { Spinner } from '@/components/atoms/Spinner';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import type { LocationType } from '@/domain/client';
import { useLocationDashboardUrl } from '@/hooks/useLocationDashboardUrl';
import { isLandbasedLocation, isSmoltLocation } from '@/utils/locationTypeHelpers';

import styles from './LocationOverviewTableFooter.module.scss';

export interface LocationOverviewTableFooterProps {
    clientId: number;
    locationId: number;
    locationType: LocationType;
    /**
     * Translation key for the footnote about wounds not being measured, or null for none. Comes
     * from the species traits, so a species added later brings its own note or no note at all.
     */
    missingWoundsNoteKey: string | null;
    /** Renders the note explaining that hidden pens live on the location page. */
    showHiddenPensNote: boolean;
    showHistoricalActions: boolean;
    hasHistoricalRows: boolean;
    showHistorical: boolean;
    onToggleShowHistorical: () => void;
    hasAccessToMarkHistorical: boolean;
    /** Tag mode is on: the historical column shows and the batch can be confirmed. */
    showMarkHistorical: boolean;
    onToggleMarkHistorical: () => void;
    /** Pens whose box differs from what is saved. */
    pendingHistoricalCount: number;
    isSubmittingHistorical: boolean;
    onConfirmHistorical: () => void;
}

export default function LocationOverviewTableFooter(props: LocationOverviewTableFooterProps) {
    const {
        clientId,
        locationId,
        locationType,
        missingWoundsNoteKey,
        showHiddenPensNote,
        showHistoricalActions,
        hasHistoricalRows,
        showHistorical,
        onToggleShowHistorical,
        hasAccessToMarkHistorical,
        showMarkHistorical,
        onToggleMarkHistorical,
        pendingHistoricalCount,
        isSubmittingHistorical,
        onConfirmHistorical,
    } = props;
    const { t } = useTranslation();
    const locationDashboardUrl = useLocationDashboardUrl(clientId, locationId);

    const isTankLocation = isSmoltLocation(locationType) || isLandbasedLocation(locationType);

    return (
        <>
            {missingWoundsNoteKey && (
                <FootNote>
                    <p>{t(missingWoundsNoteKey)}</p>
                </FootNote>
            )}

            {showHiddenPensNote && (
                <div className={styles.hiddenPensNote}>
                    <Link to={locationDashboardUrl} className={styles.hiddenPensNoteButton}>
                        <span className={styles.hiddenPensNoteText}>
                            {isTankLocation
                                ? t('organisms.locationOverviewTable.tanksWithoutBioscopesAreShown')
                                : t('organisms.locationOverviewTable.pensWithoutBioscopesAreShown')}
                        </span>
                        <SvgIcon icon={ArrowRightLine} size="custom" customSize={20} />
                    </Link>
                </div>
            )}

            {showHistoricalActions && (
                <div className={styles.actionButtons}>
                    <Button
                        size={'large'}
                        disabled={!hasHistoricalRows}
                        variant={'secondary'}
                        trackingId="location_overview_toggle_historical"
                        onClick={onToggleShowHistorical}>
                        {showHistorical
                            ? t('organisms.locationOverviewTable.hideHistorical')
                            : t('organisms.locationOverviewTable.showHistorical')}
                    </Button>

                    {hasAccessToMarkHistorical && (
                        <Button
                            size={'large'}
                            variant={'secondary'}
                            trackingId="location_overview_mark_historical"
                            disabled={isSubmittingHistorical}
                            onClick={onToggleMarkHistorical}>
                            {t('organisms.locationOverviewTable.markHistorical')}
                        </Button>
                    )}

                    {hasAccessToMarkHistorical && showMarkHistorical && (
                        <>
                            <Button
                                size={'large'}
                                variant={'primary'}
                                trackingId="location_overview_confirm_historical"
                                disabled={pendingHistoricalCount === 0 || isSubmittingHistorical}
                                onClick={onConfirmHistorical}>
                                {t('organisms.locationOverviewTable.confirmHistorical', {
                                    count: pendingHistoricalCount,
                                })}
                            </Button>
                            {isSubmittingHistorical && <Spinner />}
                        </>
                    )}
                </div>
            )}
        </>
    );
}
